import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Trash2, CheckCircle2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "pl_admin_key";

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [regs, setRegs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [msg, setMsg] = useState("");
  const [manual, setManual] = useState({ name: "", captain_name: "", city: "Girona", players_count: "" });

  const headers = useCallback(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const loadAll = useCallback(async (key) => {
    const h = { "X-Admin-Key": key };
    const [r, t] = await Promise.all([
      axios.get(`${API}/registrations`, { headers: h }),
      axios.get(`${API}/teams`),
    ]);
    setRegs(r.data);
    setTeams(t.data);
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem(KEY_STORAGE);
    if (!stored) return;
    loadAll(stored)
      .then(() => setAuthed(true))
      .catch(() => sessionStorage.removeItem(KEY_STORAGE));
  }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg("");
    try {
      await loadAll(adminKey);
      sessionStorage.setItem(KEY_STORAGE, adminKey);
      setAuthed(true);
    } catch {
      setMsg("Clave incorrecta");
    }
    setLoading(false);
  };

  const confirm = async (id) => {
    setMsg("");
    try {
      await axios.post(`${API}/admin/teams/from-registration/${id}`, {}, { headers: headers() });
      await loadAll(adminKey);
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Error al confirmar");
    }
  };

  const removeTeam = async (id) => {
    setMsg("");
    try {
      await axios.delete(`${API}/admin/teams/${id}`, { headers: headers() });
      await loadAll(adminKey);
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setMsg("");
    try {
      await axios.post(
        `${API}/admin/teams`,
        { ...manual, players_count: parseInt(manual.players_count || "0", 10) },
        { headers: headers() }
      );
      setManual({ name: "", captain_name: "", city: "Girona", players_count: "" });
      await loadAll(adminKey);
    } catch (err) {
      setMsg(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Error al crear equipo");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#0a0a0a] p-8">
          <div className="font-display text-2xl uppercase tracking-wider mb-1">
            PROXIMA<span className="text-[#EAB308]">·</span>LEAGUE
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-8">
            Panel de administración
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Clave de administrador"
            data-testid="admin-key-input"
            className="w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm outline-none focus:border-[#EAB308] mb-4"
          />
          {msg && (
            <div className="text-red-400 text-xs mb-4" data-testid="admin-login-error">{msg}</div>
          )}
          <button
            type="submit"
            disabled={loading || !adminKey}
            data-testid="admin-login-btn"
            className="w-full bg-[#EAB308] text-black px-4 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-panel">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="font-display text-3xl uppercase tracking-wider">
              Panel de <span className="text-[#EAB308]">administración</span>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mt-1">
              Solicitudes y equipos confirmados
            </div>
          </div>
          <a href="/" className="text-xs tracking-[0.2em] uppercase text-white/50 hover:text-[#EAB308]">
            ← Ir a la web
          </a>
        </div>

        {msg && (
          <div className="mb-6 border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300" data-testid="admin-msg">
            {msg}
          </div>
        )}

        {/* Solicitudes */}
        <div className="mb-12">
          <div className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">
            Solicitudes de inscripción ({regs.length})
          </div>
          <div className="border border-white/10 divide-y divide-white/5">
            {regs.length === 0 && (
              <div className="p-6 text-sm text-white/40">No hay solicitudes todavía.</div>
            )}
            {regs.map((r, idx) => (
              <div
                key={r.id}
                className="p-5 flex flex-col md:flex-row md:items-center gap-4 bg-[#0a0a0a]"
                data-testid={`admin-registration-row-${idx}`}
              >
                <div className="flex-1">
                  <div className="font-display text-xl uppercase">{r.team_name}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {r.captain_name} · {r.phone} · {r.email} · {r.city} · {r.players_count} jugadores
                    {r.marketing_accepted && (
                      <span className="text-green-400" data-testid={`admin-marketing-badge-${idx}`}> · acepta comunicaciones</span>
                    )}
                  </div>
                  {r.comments && <div className="text-xs text-white/35 mt-1 italic">"{r.comments}"</div>}
                </div>
                {r.confirmed ? (
                  <span className="inline-flex items-center gap-2 text-green-400 text-xs tracking-[0.2em] uppercase font-bold" data-testid={`admin-confirmed-badge-${idx}`}>
                    <CheckCircle2 className="w-4 h-4" /> Confirmado
                  </span>
                ) : (
                  <button
                    onClick={() => confirm(r.id)}
                    data-testid={`admin-confirm-btn-${idx}`}
                    className="bg-[#EAB308] text-black px-4 py-2 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-colors"
                  >
                    Confirmar equipo
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Equipos confirmados */}
        <div className="mb-12">
          <div className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">
            Equipos confirmados ({teams.length}/16)
          </div>
          <div className="border border-white/10 divide-y divide-white/5">
            {teams.length === 0 && (
              <div className="p-6 text-sm text-white/40">No hay equipos confirmados.</div>
            )}
            {teams.map((t, idx) => (
              <div
                key={t.id}
                className="p-5 flex items-center gap-4 bg-[#0a0a0a]"
                data-testid={`admin-team-row-${idx}`}
              >
                <div className="flex-1">
                  <div className="font-display text-xl uppercase">{t.name}</div>
                  <div className="text-xs text-white/50 mt-1">
                    {t.captain_name} · {t.city} ·{" "}
                    <Link to={`/equipos/${t.slug}`} className="text-[#EAB308] hover:underline" data-testid={`admin-team-link-${idx}`}>
                      /equipos/{t.slug}
                    </Link>
                  </div>
                </div>
                <button
                  onClick={() => removeTeam(t.id)}
                  data-testid={`admin-delete-btn-${idx}`}
                  className="border border-red-500/40 text-red-400 p-2 hover:bg-red-500/10 transition-colors"
                  aria-label="Eliminar equipo"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Añadir manual */}
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#EAB308] mb-4">
            Añadir equipo manualmente
          </div>
          <form onSubmit={addManual} className="border border-white/10 bg-[#0a0a0a] p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-add-form">
            <input
              value={manual.name}
              onChange={(e) => setManual({ ...manual, name: e.target.value })}
              placeholder="Nombre del equipo"
              required
              data-testid="admin-add-name"
              className="bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm outline-none focus:border-[#EAB308]"
            />
            <input
              value={manual.captain_name}
              onChange={(e) => setManual({ ...manual, captain_name: e.target.value })}
              placeholder="Capitán"
              required
              data-testid="admin-add-captain"
              className="bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm outline-none focus:border-[#EAB308]"
            />
            <input
              value={manual.city}
              onChange={(e) => setManual({ ...manual, city: e.target.value })}
              placeholder="Ciudad"
              required
              data-testid="admin-add-city"
              className="bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm outline-none focus:border-[#EAB308]"
            />
            <input
              type="number"
              min={0}
              max={40}
              value={manual.players_count}
              onChange={(e) => setManual({ ...manual, players_count: e.target.value })}
              placeholder="Nº jugadores"
              data-testid="admin-add-players"
              className="bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm outline-none focus:border-[#EAB308]"
            />
            <button
              type="submit"
              data-testid="admin-add-submit"
              className="bg-[#EAB308] text-black px-4 py-2.5 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Añadir
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
